import { PackageUnitDto } from '@nest/domain-entities-metadata-generated';
import { Command } from '@nest/internal-frontend';
import { Skeleton, useDialogs, useScanner, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';
import { useCommandCompleteConsignmentNote } from './useCommandCompleteConsignmentNote.hook';
import { useCommandPrintConsignmentNote } from './useCommandPrintConsignmentNote.hook';

export default function ConsignmentNoteDetailPage() {
	const consignmentNoteId = useNumberParam('consignmentNoteId');
	const client = useTerminalClient();
	const queryClient = useQueryClient();
	const listRef = useRef<HTMLDivElement>(null);
	const { confirm } = useDialogs();
	const [lastScannedPackageUnit, setLastScannedPackageUnit] = useState<PackageUnitDto | null>(null);
	const scanner = useScanner();

	const { data: consignmentNote } = useQuery({
		queryKey: ['consignment-note', consignmentNoteId],
		queryFn: () => client.consignmentNotes.getConsignmentNoteByIdQuery.execute({ id: consignmentNoteId!, populate: ['lines.packageUnit'] }),
		enabled: !!consignmentNoteId
	});

	const addByScanMutation = useMutation({
		mutationFn: (code: string) => client.consignmentNotes.addPackageUnitToConsignmentNoteMutation.execute({ consignmentNoteId: consignmentNoteId!, code }),
		onSuccess: async result => {
			if (result?.packageUnit) {
				setLastScannedPackageUnit(result.packageUnit);
			}

			await queryClient.invalidateQueries({ queryKey: ['consignment-note', consignmentNoteId] });

			if (listRef.current) {
				listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
			}
		}
	});

	const addByScan = addByScanMutation.mutateAsync;

	const removeMutation = useMutation({
		mutationFn: (packageUnitId: number) =>
			client.consignmentNotes.removePackageUnitFromConsignmentNoteMutation.execute({ consignmentNoteId: consignmentNoteId!, packageUnitId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['consignment-note', consignmentNoteId] });
		}
	});

	useValidationResultDialog([addByScanMutation.error, removeMutation.error]);

	const onRemovePackageUnit = async (packageUnit: PackageUnitDto) => {
		if (await confirm({ message: `Opravdu chcete odebrat balík ${packageUnit.parcelNumber}?`, title: 'Odebrání balíku' })) {
			removeMutation.mutateAsync(packageUnit.id);
		}
	};

	useEffect(() => {
		if (scanner.code) {
			void addByScan(scanner.code);
		}
	}, [scanner.code, addByScan]);

	const completeCommand = useCommandCompleteConsignmentNote();
	const printCommand = useCommandPrintConsignmentNote();

	const commands: Command[] = consignmentNote?.isCompleted ? [printCommand] : [completeCommand, printCommand];

	return (
		<TerminalScreen showBackButton topBar={<div>{consignmentNote?.containerNumber}</div>} commands={commands}>
			<div ref={listRef}>
				{lastScannedPackageUnit && (
					<div className="mb-4 p-2 bg-orange-200 text-amber-900 text-md text-center">
						<div>Poslední naskenovaný balík</div>
						<div className="font-bold text-xl">{lastScannedPackageUnit.parcelNumber}</div>
					</div>
				)}
				<table className="w-full text-left">
					<thead>
						<tr>
							<th className="w-full sticky top-0 bg-background p-2">Naskenované balíky</th>
						</tr>
					</thead>
					<tbody>
						{consignmentNote ? (
							consignmentNote.lines.map(line => (
								<tr key={line.id} className="border-b last:border-b-0">
									<td className="py-4 px-2" onClick={() => onRemovePackageUnit(line.packageUnit)}>
										{line.packageUnit!.parcelNumber}
									</td>
								</tr>
							))
						) : (
							<tr>
								<td className="py-2">
									<div className="space-y-2">
										<Skeleton className="h-6 w-full" />
										<Skeleton className="h-6 w-full" />
										<Skeleton className="h-6 w-full" />
									</div>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</TerminalScreen>
	);
}
